import { useCallback, useState } from 'react';
import { Route, Routes } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import Badge from '@/design_system/Badge/Badge';
import Button from '@/design_system/Button';
import InputSearch from '@/design_system/InputSearch';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import {
  getWorkflowStoreTypeLabel,
  getWorkflowTypeLabel,
  useWorkflows,
  WorkflowListItem,
} from '@/models/workflow';
import { formatDate } from '@/utils/date';
import useDebouncedState from '@/utils/useDebouncedState';

import { CreateWorkflowDialog } from './components/CreateWorkflowDialog';
import { Workflow as WorkflowPage } from './Workflow/Workflow';

const WORKFLOWS_PER_PAGE = 20;

export const WorkflowsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<WorkflowsList />} />
      <Route path="/:id/*" element={<WorkflowPage />} />
    </Routes>
  );
};

const WorkflowsList = () => {
  const { t } = useLingui();
  const [page, setPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const resetPage = useCallback(() => setPage(1), []);
  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const {
    data: { workflows, meta } = { workflows: [] },
    isLoading: isWorkflowsLoading,
    isPlaceholderData,
    isError: isWorkflowsError,
  } = useWorkflows(
    {
      limit: WORKFLOWS_PER_PAGE,
      offset: (page - 1) * WORKFLOWS_PER_PAGE,
      search: debouncedSearch || undefined,
    },
    { keepPreviousData: true }
  );

  return (
    <main style={{ gap: '1rem' }}>
      <Stack row gap="1rem" alignItems="center">
        <InputSearch
          ariaLabel={t({
            id: 'settings.organization.workflows.workflows.search.label',
            message: 'Search workflows',
          })}
          placeholder={t({
            id: 'settings.organization.workflows.workflows.search.placeholder',
            message: 'Search workflows...',
          })}
          value={search}
          onChange={debouncedSetSearch}
          style={{ flex: 1 }}
        />
        <Button variant="secondary" size="medium" onPress={() => setIsCreateDialogOpen(true)}>
          <IconAdd />
          <Trans id="settings.organization.workflows.workflows.create.button">
            Create workflow
          </Trans>
        </Button>
      </Stack>

      <CreateWorkflowDialog isOpen={isCreateDialogOpen} setIsOpen={setIsCreateDialogOpen} />

      <TableQueryWrapper
        isLoading={isWorkflowsLoading || isPlaceholderData}
        isError={isWorkflowsError}
      >
        {workflows.length === 0 ? (
          <EmptyState
            subtitle={
              <Paragraph>
                <Trans id="settings.organization.workflows.workflows.no-results">
                  No workflows.
                </Trans>
              </Paragraph>
            }
            illustration={<IllustrationNoResults />}
          />
        ) : (
          <Table columnWidths={['2fr', '2fr', '1fr', '1fr']} bordered>
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.organization.workflows.workflows.table.header.name">
                    Name
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.workflows.workflows.table.header.type">
                    Type
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.workflows.workflows.table.header.last-updated">
                    Last updated
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.workflows.workflows.table.header.active-requests">
                    Active requests
                  </Trans>
                </Column>
              </Row>
            </Header>

            <Body>
              {workflows.map((workflow) => (
                <WorkflowRow key={workflow.id} workflow={workflow} />
              ))}
            </Body>

            <PaginationFooter
              itemsPerPage={WORKFLOWS_PER_PAGE}
              page={page}
              onPageChange={setPage}
              count={meta?.count}
            />
          </Table>
        )}
      </TableQueryWrapper>
    </main>
  );
};

const WorkflowRow = ({ workflow }: { workflow: WorkflowListItem }) => {
  const { i18n } = useLingui();

  const typeLabel = i18n._(getWorkflowTypeLabel(workflow.type));
  const showStoreTypes =
    (workflow.type === 'store' || workflow.type === 'client_via_store') &&
    workflow.storeTypes.length > 0;
  const storeTypesLabel = workflow.storeTypes
    .map((storeType: WorkflowListItem['storeTypes'][number]) =>
      i18n._(getWorkflowStoreTypeLabel(storeType))
    )
    .join(', ');
  const updatedAt = new Date(String(workflow.updatedAt));

  return (
    <Row to={`/settings_beta/workflows/workflows/${workflow.id}`}>
      <Cell isLink>
        <Stack row gap="0.5rem" alignItems="center">
          <Paragraph weight="medium">
            {workflow.name ?? (
              <Trans id="settings.organization.workflows.workflows.unnamed">Unnamed workflow</Trans>
            )}
          </Paragraph>
          {workflow.enabled && (
            <Badge size="x-small" color="primary">
              <Trans id="settings.organization.workflows.workflows.enabled">Enabled</Trans>
            </Badge>
          )}
        </Stack>
      </Cell>
      <Cell>
        <Paragraph size="200">
          {showStoreTypes ? `${typeLabel} (${storeTypesLabel})` : typeLabel}
        </Paragraph>
      </Cell>
      <Cell>
        <Paragraph size="200">{formatDate(updatedAt, { dateStyle: 'medium' })}</Paragraph>
      </Cell>
      <Cell>
        <Paragraph size="200">{workflow.activeRequestsCount}</Paragraph>
      </Cell>
    </Row>
  );
};
